import { RotateCcw, SlidersHorizontal, X, Radio, Layers3 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMapStore } from '../../store/mapStore.js';
import CategoryFilter from '../map/CategoryFilter.js';

interface FilterPanelProps {
  activeCategory: string | null;
  onCategoryChange: (category: string | null) => void;
}

export default function FilterPanel({ activeCategory, onCategoryChange }: FilterPanelProps) {
  const { filters, showFilters, setFilters, toggleFilters } = useMapStore();
  const activeCount = Number(filters.openNow) + Number(filters.minRating > 0) + Number(filters.sortBy !== 'distance') + Number(activeCategory !== null);

  return (
    <>
      <button
        type="button"
        onClick={toggleFilters}
        className={`filter-trigger ${showFilters ? 'is-active' : ''}`}
        aria-expanded={showFilters}
      >
        <SlidersHorizontal size={15} />
        <span>LAYERS</span>
        {activeCount > 0 && <span className="system-count">{String(activeCount).padStart(2, '0')}</span>}
      </button>

      <AnimatePresence>
      {showFilters && (
        <motion.aside
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="filter-panel"
          aria-label="Map data layers"
        >
          <div className="system-panel-header">
            <div>
              <span className="system-kicker"><Radio size={11} /> QUERY CONTROL</span>
              <strong>DATA LAYERS</strong>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => { setFilters({ openNow: false, minRating: 0, sortBy: 'distance' }); onCategoryChange(null); }} className="system-icon-button" aria-label="Reset filters"><RotateCcw size={13} /></button>
              <button type="button" onClick={toggleFilters} className="system-icon-button" aria-label="Close filters"><X size={14} /></button>
            </div>
          </div>

          <div className="system-section">
            <div className="system-section-label"><Layers3 size={12} /> OSM CATEGORY</div>
            <CategoryFilter activeCategory={activeCategory} onCategoryChange={onCategoryChange} />
          </div>

          <div className="system-section">
            <div className="system-section-label">AVAILABILITY</div>
            <label className="mechanical-toggle-row">
              <span><strong>OPEN_NOW</strong><small>Live operational status</small></span>
              <input
                type="checkbox"
                checked={filters.openNow}
                onChange={(e) => setFilters({ openNow: e.target.checked })}
              />
              <span className="mechanical-toggle" aria-hidden="true" />
            </label>
          </div>

          <div className="system-section">

            <div>
              <div className="system-section-label">MINIMUM RATING</div>
              <div className="system-segments">
                {[0, 3, 4, 4.5].map((r) => (
                  <button
                    key={r}
                    onClick={() => setFilters({ minRating: r })}
                    className={`system-segment ${
                      filters.minRating === r
                        ? 'is-active'
                        : ''
                    }`}
                  >
                    {r === 0 ? 'Any' : `${r}+`}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="system-section-label">SORT PIPELINE</div>
              <div className="system-segments">
                {([
                  { value: 'distance', label: 'Distance' },
                  { value: 'rating', label: 'Rating' },
                  { value: 'name', label: 'Name' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setFilters({ sortBy: opt.value })}
                    className={`system-segment ${
                      filters.sortBy === opt.value
                        ? 'is-active'
                        : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="system-panel-footer">
            <span>ACTIVE_QUERY</span>
            <strong>{String(activeCount).padStart(2, '0')}</strong>
          </div>
        </motion.aside>
      )}
      </AnimatePresence>
    </>
  );
}
